import { useCallback } from 'react'
import { useAuth } from 'context/authentication/useAuth'
import { Outlet, useSearchParams } from 'react-router-dom'
import styled from 'styled-components/macro'
import { UnifiedSearchModal } from 'components/UnifiedSearch'
import {
  LeftNavBar,
  SlideOutPanel,
  useLeftNavStore,
} from 'components/Layout/LeftNav'
import { Header } from 'components/Layout/Header'
import { NewFooter } from 'components/Layout/Footer/NewFooter'
import { AuthenticatedNav } from 'components/Layout/Navbar'
import Footer from 'components/Layout/Footer/Footer'
import { featureFlagService } from 'utils/featureFlagService'
import { LayoutConfigProvider, useLayoutConfig } from 'context/layout'
import { cn } from 'components/UI/cn'
import { ErrorBoundary } from 'components/UI/ErrorBoundary'

export function AppLayout() {
  const { enableNewNavigation } = featureFlagService()

  if (enableNewNavigation) {
    return (
      <ErrorBoundary fallback={<OldAppLayout />}>
        <LayoutConfigProvider>
          <NewAppLayout />
        </LayoutConfigProvider>
      </ErrorBoundary>
    )
  }

  return <OldAppLayout />
}

// OLD NAVIGATION LAYOUT (when feature flag is disabled)
function OldAppLayout() {
  const { isAuthenticated } = useAuth()

  return (
    <OldLayoutRoot>
      {isAuthenticated && <AuthenticatedNav />}
      <OldContent id="app-content-container">
        <Outlet />
      </OldContent>
      <Footer />
      {isAuthenticated && <UnifiedSearchModal />}
    </OldLayoutRoot>
  )
}

// NEW NAVIGATION LAYOUT (when feature flag is enabled)
function NewAppLayout() {
  const { isAuthenticated } = useAuth()
  const { isCollapsed, isSlideOutOpen, activeSection } = useLeftNavStore()
  const [, setSearchParams] = useSearchParams()
  const { config } = useLayoutConfig()

  const openSearch = useCallback(() => {
    setSearchParams((prev) => {
      prev.set('search', 'true')
      return prev
    })
  }, [setSearchParams])

  const showSlideOut = isAuthenticated && isSlideOutOpen && activeSection

  const hasLeftNav = isAuthenticated && !isCollapsed

  return (
    <div
      className="flex flex-col h-screen w-full overflow-hidden bg-[var(--nav-bg-primary)]"
      data-new-nav="true"
    >
      {/* Top Header - spans full width */}
      {isAuthenticated && <Header onSearchClick={openSearch} />}

      {/* Main Area: Left Nav + Content */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Left Navigation Bar */}
        {isAuthenticated && <LeftNavBar />}

        {/* App Shell with border effect */}
        <div
          className={cn(
            'flex flex-row flex-1 min-w-0 m-[var(--app-border-width)] bg-[#f9fafb] rounded-xl overflow-hidden',
            hasLeftNav ? 'ml-0' : 'ml-[var(--app-border-width)]'
          )}
        >
          {/* Slide-Out Panel - inside AppShell, pushes content */}
          {isAuthenticated && <SlideOutPanel $isOpen={!!showSlideOut} />}

          <div className="flex flex-col flex-1 min-w-0">
            <div
              id="app-content-container"
              className="flex-1 flex flex-col overflow-auto"
            >
              <Outlet />
            </div>
            {/* Hide footer on pages that request it (e.g., company pages) */}
            {!config.hideFooter && <NewFooter />}
          </div>
        </div>
      </div>

      {isAuthenticated && <UnifiedSearchModal />}
    </div>
  )
}

// OLD LAYOUT STYLED COMPONENTS (when feature flag is disabled)
const OldLayoutRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100%;
  overflow: hidden;
`

const OldContent = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: auto;
`
